// O ajuste mês a mês sobre a referência (design D21, spec target-month-adjustments): as contas
// puras do modo "Cronograma atual" (o ajuste mês a mês) do editor. A referência é a ORIGEM do alvo (desembolso do
// ERP, previsto ou físico-financeiro); o cliente muda o início, e a programação inteira anda
// junto, ou ajusta as parcelas com os mesmos atalhos do novo cronograma.
//
// O "Personalizado" é uma linha por mês da janela da referência, em centavos e com a
// defasagem contada do início. Ao gravar, ele vira uma de três formas (`referenceSaveOf`):
// - linhas iguais à referência: só o deslocamento, sem perfil (acompanha a origem quando ela
//   recarrega);
// - linhas mexidas: o perfil por peso (o valor de cada linha), exato no centavo;
// - atalho de parcelas: a forma em bps, como no novo cronograma.
import { allocateProportional } from "./allocate";
import { compareMonthKey, parseMonthKey, shiftMonth } from "./hierarchy";
import { monthsBetween, seriesMap } from "./monthEdit";
import {
  MIN_OFFSET,
  WEIGHTED_MAX_LINES as MAX_LINES,
  WEIGHTED_MAX_OFFSET as MAX_OFFSET,
} from "./profile";

const monthIndex = (month) => {
  const { year, month: number } = parseMonthKey(month);
  return year * 12 + (number - 1);
};

/** Meses de `from` até `to` (negativo quando `to` vem antes). */
export const monthDistance = (from, to) => monthIndex(to) - monthIndex(from);

/**
 * A referência do alvo: `Map<mês, centavos>` sem zeros, o primeiro e o último mês com valor e
 * o total. `series` aceita `[{ month|competence, cents }]` ou `[["AAAA-MM", centavos]]`.
 */
export const referenceOf = (series) => {
  const map = seriesMap(series);
  const months = [...map.keys()].sort(compareMonthKey);
  let totalCents = 0;
  for (const cents of map.values()) totalCents += cents;
  return {
    map,
    first: months[0] ?? null,
    last: months[months.length - 1] ?? null,
    totalCents,
    // Meses da janela, do primeiro ao último com valor, contando os vazios do meio.
    span: months.length > 0 ? monthsBetween(months[0], months[months.length - 1]).length : 0,
  };
};

/** A referência cabe no "Personalizado"? (as linhas seguem `MAX_LINES` e a faixa de defasagem) */
// O cronograma em valor tem janela própria (revisão de 2026-10-07): até 120 meses.
export const fitsCustom = (reference) => reference.span > 0
  && reference.span <= MAX_LINES
  && reference.span - 1 <= MAX_OFFSET;

let lineSequence = 0;
const withId = (line) => {
  lineSequence += 1;
  return { lineId: `ref-line-${lineSequence}`, ...line };
};

/** As linhas da referência: uma por mês da janela, defasagem a partir do primeiro mês. */
export const referenceLines = (reference) => (reference.first === null
  ? []
  : monthsBetween(reference.first, reference.last).map((month, offset) => withId({
    offset,
    cents: reference.map.get(month) ?? 0,
  })));

/**
 * As linhas de um perfil por peso gravado, reescaladas para o total da referência de hoje
 * (`allocateProportional`, o mesmo maior resto do motor): reabrir um ajuste depois de a
 * origem recarregar mostra o que o motor faz agora, com o saldo em zero.
 */
export const weightedLines = (profile, totalCents) => {
  const lines = (Array.isArray(profile) ? profile : []).filter((line) => Number.isSafeInteger(line?.weight));
  if (lines.length === 0) return [];
  const cents = totalCents > 0
    ? allocateProportional(totalCents, lines.map((line) => line.weight))
    : lines.map((line) => line.weight);
  return lines.map((line, index) => withId({ offset: line.offsetMonths ?? line.offset, cents: cents[index] }));
};

/** Soma das linhas. */
export const linesTotal = (lines) => lines.reduce(
  (total, line) => total + (Number.isSafeInteger(line.cents) ? line.cents : 0),
  0,
);

/** As linhas são as da referência, mês a mês? (zeros não contam) */
export const sameAsReference = (lines, reference) => {
  const own = new Map();
  for (const line of lines) {
    if (line.cents !== 0) own.set(line.offset, line.cents);
  }
  if (reference.first === null) return own.size === 0;
  if (own.size !== reference.map.size) return false;
  for (const [month, cents] of reference.map) {
    if (own.get(monthDistance(reference.first, month)) !== cents) return false;
  }
  return true;
};

/**
 * "Adiar para o mês seguinte": o valor inteiro da linha vai para a linha da defasagem
 * seguinte, criada quando não existe. Devolve as linhas novas, em ordem de defasagem.
 */
export const postponeLine = (lines, lineId) => {
  const line = lines.find((candidate) => candidate.lineId === lineId);
  if (!line || !(line.cents > 0)) return lines;
  const nextOffset = line.offset + 1;
  if (nextOffset > MAX_OFFSET) return lines;
  const hasNext = lines.some((candidate) => candidate.offset === nextOffset);
  const moved = lines.map((candidate) => {
    if (candidate.lineId === lineId) return { ...candidate, cents: 0 };
    if (candidate.offset === nextOffset) return { ...candidate, cents: (candidate.cents ?? 0) + line.cents };
    return candidate;
  });
  const withNext = hasNext ? moved : [...moved, withId({ offset: nextOffset, cents: line.cents })];
  return withNext.sort((left, right) => left.offset - right.offset);
};

/** Uma linha nova depois da última, com valor zero. */
export const addLine = (lines) => {
  if (lines.length >= MAX_LINES) return lines;
  const last = lines.reduce((max, line) => Math.max(max, line.offset), -1);
  return [...lines, withId({ offset: last + 1, cents: 0 })];
};

/**
 * O que está errado nas linhas do "Personalizado", ou `null`. O saldo é tratado à parte:
 * a tela mostra quanto falta redistribuir.
 */
export const linesProblem = (lines) => {
  if (lines.length > MAX_LINES) return `O ajuste aceita no máximo ${MAX_LINES} parcelas`;
  const seen = new Set();
  for (const line of lines) {
    if (!Number.isSafeInteger(line.offset)) return "Cada parcela precisa de uma competência";
    if (line.offset < MIN_OFFSET || line.offset > MAX_OFFSET) {
      return `As parcelas devem ficar entre ${MIN_OFFSET} e ${MAX_OFFSET} meses do início`;
    }
    if (seen.has(line.offset)) return "Cada competência só pode aparecer uma vez";
    seen.add(line.offset);
    if (!Number.isSafeInteger(line.cents) || line.cents < 0) return "O valor de cada parcela precisa ser zero ou positivo";
  }
  if (!lines.some((line) => line.cents > 0)) return "Ao menos uma parcela precisa de valor";
  return null;
};

/**
 * O que o salvar grava (D21). `startMonth` é o início efetivo. Devolve `{ profile }`:
 * `null` (deslocamento), o perfil por peso ou o perfil em bps do atalho.
 */
export const referenceSaveOf = ({ shortcut, lines, reference, shortcutProfile, allowShift = true }) => {
  if (shortcut !== "custom") return { profile: shortcutProfile ?? null, kind: "shortcut" };
  // `allowShift: false` quando a referência é o resultado de uma regra, não a origem: o
  // deslocamento sem perfil desloca a ORIGEM, então ali grava sempre o valor por mês.
  if (allowShift && sameAsReference(lines, reference)) return { profile: null, kind: "shift" };
  return {
    profile: lines
      .filter((line) => line.cents > 0)
      .sort((left, right) => left.offset - right.offset)
      .map((line) => ({ offsetMonths: line.offset, weight: line.cents })),
    kind: "weighted",
  };
};

/** A competência de cada linha, a partir do início. */
export const lineMonth = (startMonth, line) => (
  startMonth && Number.isSafeInteger(line.offset) ? shiftMonth(startMonth, line.offset) : null
);

// --- "Ajustar meses" (revisão de 2026-10-07): as mesmas linhas, vistas por competência ------
// A tabela de meses e o "Personalizado" editam o MESMO estado (as linhas em centavos, com a
// defasagem contada do início). Aqui ficam as contas por mês: o que a tabela mostra, pôr um
// valor num mês, adiar um mês inteiro e mover parte de um mês para outro.

/** `Map<mês, centavos>` do ajustado: cada linha na competência dela a partir do início. */
export const adjustedByMonth = (lines, startMonth) => {
  const byMonth = new Map();
  for (const line of lines ?? []) {
    const month = lineMonth(startMonth, line);
    if (month && Number.isSafeInteger(line.cents)) byMonth.set(month, (byMonth.get(month) ?? 0) + line.cents);
  }
  return byMonth;
};

/**
 * Os meses da tabela: contínuos, do primeiro ao último mês com valor na referência ou no
 * ajustado, mais as linhas vazias que o cliente incluiu.
 */
export const monthRows = (reference, lines, startMonth) => {
  const months = [
    ...reference.map.keys(),
    ...(lines ?? []).map((line) => lineMonth(startMonth, line)).filter(Boolean),
  ].sort(compareMonthKey);
  if (months.length === 0) return [];
  return monthsBetween(months[0], months[months.length - 1]);
};

const offsetOfMonth = (startMonth, month) => monthDistance(startMonth, month);

/** O valor ajustado de um mês passa a ser `cents` (cria a linha quando o mês não tem). */
export const setMonthCents = (lines, startMonth, month, cents) => {
  const offset = offsetOfMonth(startMonth, month);
  if (lines.some((line) => line.offset === offset)) {
    return lines.map((line) => (line.offset === offset ? { ...line, cents } : line));
  }
  return [...lines, withId({ offset, cents })].sort((left, right) => left.offset - right.offset);
};

/** Move `cents` de um mês para outro, nunca mais do que o mês tem. */
export const moveMonthCents = (lines, startMonth, from, to, cents) => {
  if (from === to) return lines;
  const available = adjustedByMonth(lines, startMonth).get(from) ?? 0;
  const amount = Math.min(Math.max(0, cents), available);
  if (amount === 0) return lines;
  const after = setMonthCents(lines, startMonth, from, available - amount);
  const target = adjustedByMonth(after, startMonth).get(to) ?? 0;
  return setMonthCents(after, startMonth, to, target + amount);
};

/** "Adiar para o mês seguinte": o mês inteiro vai para o próximo. */
export const postponeMonth = (lines, startMonth, month) => moveMonthCents(
  lines, startMonth, month, shiftMonth(month, 1), adjustedByMonth(lines, startMonth).get(month) ?? 0,
);
